/* RESPONSIBLE TEAM: team-email */
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { isValidEmail } from 'embercom/lib/email';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ajax from 'embercom/lib/ajax';
import type IntlService from 'ember-intl/services/intl';

export type ValidatedRow = {
  value: string;
  reason: string | undefined;
};
export type ValidatedColumn = {
  hasInvalidRow: boolean;
  header: string;
  rows: ValidatedRow[];
};

export default class CsvImportAdvanced extends Controller {
  queryParams = [''];

  @service declare router: RouterService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @tracked isDataValid: boolean | undefined;
  @tracked validatedFile: ValidatedColumn[] | undefined;
  @tracked missingHeaderList: string | undefined;
  @tracked unrecognizedHeaderList: string | undefined;
  @tracked csvMalformedError: string | undefined;
  @tracked hasNoDataRows = false;

  declare model: $TSFixMe;

  @action processValues() {
    // Reset validation state for new CSV upload
    this.isDataValid = undefined;
    this.missingHeaderList = undefined;
    this.unrecognizedHeaderList = undefined;
    this.csvMalformedError = undefined;
    this.hasNoDataRows = false;
    this.validatedFile = undefined;

    let validCsv = true;
    let existingInboxes = this.model.existingTeamInboxes
      .toArray()
      .map((inbox: any) => inbox.name.toLowerCase());

    let inboxEmojiMap: Map<string, string> = new Map(
      this.model.existingTeamInboxes
        .toArray()
        .map((inbox: any) => [inbox.name.toLowerCase(), inbox.avatar_emoji]),
    );

    this.validateCsvStructure();
    if (this.isDataValid !== false) {
      let headers: Set<string> = new Set(
        this.model.columns.map((col: any) => col[0].toLowerCase()),
      );
      this.validateHeaders(headers);
      this.validateDataRows();

      // Always process columns if CSV structure is valid (for UI display)
      // Extract primary and secondary inbox columns for cross-validation
      let primaryInboxColumn = this.model.columns.find(
        (col: any) => col[0] === 'primary_team_inboxes',
      );
      let secondaryInboxColumn = this.model.columns.find(
        (col: any) => col[0] === 'secondary_team_inboxes',
      );
      let primaryInboxValues = primaryInboxColumn ? primaryInboxColumn.slice(1) : [];
      let secondaryInboxValues = secondaryInboxColumn ? secondaryInboxColumn.slice(1) : [];

      let validatedFile = this.model.columns.map((column: any) => {
        let header = column[0];
        let values = column.slice(1, column.length);
        let validatedColumn = undefined;
        switch (header) {
          case 'email':
            validatedColumn = this.parseEmails(values);
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          case 'role':
            validatedColumn = this.parseRoles(values);
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          case 'primary_team_inboxes':
            validatedColumn = this.parseInboxes(
              values,
              'primary_team_inboxes',
              existingInboxes,
              inboxEmojiMap,
              secondaryInboxValues,
            );
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          case 'secondary_team_inboxes':
            validatedColumn = this.parseInboxes(
              values,
              'secondary_team_inboxes',
              existingInboxes,
              inboxEmojiMap,
              primaryInboxValues,
            );
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          case 'copilot_usage':
            validatedColumn = this.parseCopilotUsage(values);
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          case 'conversation_assignment_limit':
            validatedColumn = this.parseAssignmentLimit(values, header);
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          case 'ticket_assignment_limit':
            validatedColumn = this.parseAssignmentLimit(values, header);
            if (validatedColumn.hasInvalidRow) {
              validCsv = false;
            }
            return validatedColumn;
          default:
            return this.genericRow(values, header);
        }
      });

      // Update final validation state based on all checks
      if (this.isDataValid) {
        this.isDataValid = validCsv;
      }
      this.validatedFile = validatedFile;
    }
  }

  validateHeaders(headers: Set<string>): void {
    let expectedHeaders: Set<string> = new Set([
      'email',
      'role',
      'primary_team_inboxes',
      'secondary_team_inboxes',
      'conversation_assignment_limit',
      'ticket_assignment_limit',
      'copilot_usage',
    ]);
    let missingHeaders = [...expectedHeaders].filter((x) => !headers.has(x));
    let unrecognizedHeaders = [...headers].filter((x) => !expectedHeaders.has(x));

    if (missingHeaders.length > 0) {
      this.missingHeaderList = missingHeaders.join(', ');
    }
    if (unrecognizedHeaders.length > 0) {
      this.unrecognizedHeaderList = unrecognizedHeaders.join(', ');
    }

    this.isDataValid = !(unrecognizedHeaders.length > 0 || missingHeaders.length > 0);
  }

  validateCsvStructure(): void {
    // Check if columns array exists and is valid
    if (!this.model.columns || !Array.isArray(this.model.columns)) {
      this.csvMalformedError = this.intl.t(
        'settings.teammates.invite.csv-import.advanced.csv-malformed',
      );
      this.isDataValid = false;
      return;
    }

    // Check for jagged rows (inconsistent column counts) and undefined values from missing data
    if (this.model.columns.length > 0) {
      let expectedLength = this.model.columns[0]?.length || 0;
      let hasJaggedRows = this.model.columns.some(
        (col: any) => !Array.isArray(col) || col.length !== expectedLength,
      );

      // Check for undefined values that indicate missing data in CSV
      let hasUndefinedValues = this.model.columns.some(
        (col: any) => Array.isArray(col) && col.some((value: any) => value === undefined),
      );

      if (hasJaggedRows || hasUndefinedValues) {
        this.csvMalformedError = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.csv-malformed',
        );
        this.isDataValid = false;
      }
    }
  }

  validateDataRows(): void {
    // Check if any column has data rows (excluding header)
    let hasDataRows = this.model.columns.some((column: any) => {
      let dataRows = column.slice(1);
      return dataRows.length > 0 && dataRows.some((row: string) => row.trim().length > 0);
    });

    this.hasNoDataRows = !hasDataRows;

    // Update isDataValid to false if no data rows or if already invalid
    if (this.hasNoDataRows) {
      this.isDataValid = false;
    }
  }

  parseEmails(emailRows: string[]): ValidatedColumn {
    let hasInvalidRow = false;
    let existingTeammates = this.model.existingTeammates.toArray();
    let invitedTeammates = this.model.existingInvites.toArray();
    let seenEmails = new Set<string>();
    let validatedRows: ValidatedRow[] = emailRows.map((row) => {
      let reason = undefined;
      if (!isValidEmail(row)) {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.invalid-email',
        );
      }
      if (existingTeammates.any((address: any) => address.email === row)) {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.existing-teammate',
        );
      }
      if (invitedTeammates.any((address: any) => address.email === row && address.active)) {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.existing-invite',
        );
      }
      if (seenEmails.has(row)) {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.duplicated-invite',
        );
      }
      if (row.trim() === '') {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.empty-email',
        );
      }
      seenEmails.add(row);
      if (reason) {
        hasInvalidRow = true;
      }
      return { value: row, reason };
    });
    return { hasInvalidRow, header: 'email', rows: validatedRows };
  }

  parseRoles(rolesRows: string[]): ValidatedColumn {
    let hasInvalidRow = false;
    let existingRoles = this.model.existingRoles.toArray();
    let validatedRows: ValidatedRow[] = rolesRows.map((row) => {
      let reason = undefined;
      if (!existingRoles.any((role: any) => role.name.toLowerCase() === row.toLowerCase())) {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.invalid-role',
        );
        hasInvalidRow = true;
      }
      return { value: row, reason };
    });
    return { hasInvalidRow, header: 'role', rows: validatedRows };
  }

  parseInboxes(
    inboxRows: string[],
    headerName: string,
    existingInboxes: any[],
    inboxEmojiMap: Map<string, string>,
    otherColumnInboxValues: string[],
  ): ValidatedColumn {
    let hasInvalidRow = false;
    let validatedRows: ValidatedRow[] = inboxRows.map((row, index) => {
      let reason = undefined;
      let currentInboxes = row
        .split(',')
        .map((rowInbox) => rowInbox.trim().toLowerCase())
        .filter((inbox) => inbox.length > 0);

      // Check if any inbox in current row exists in the other column for the same row
      if (otherColumnInboxValues[index]) {
        let otherInboxes = otherColumnInboxValues[index]
          .split(',')
          .map((inbox) => inbox.trim().toLowerCase())
          .filter((inbox) => inbox.length > 0);

        let overlappingInboxes = currentInboxes.filter((inbox) => otherInboxes.includes(inbox));
        if (overlappingInboxes.length > 0) {
          reason = this.intl.t(
            'settings.teammates.invite.csv-import.advanced.validation.inbox-on-both-assignments',
            {
              inboxNames: overlappingInboxes.join(', '),
            },
          );
          hasInvalidRow = true;
        }
      }

      // Only check for non-existent inboxes if no overlap was found
      if (!reason) {
        let nonExistentInboxes = currentInboxes.filter((inbox) => !existingInboxes.includes(inbox));

        switch (nonExistentInboxes.length) {
          case 0:
            break;
          case 1:
            reason = this.intl.t(
              'settings.teammates.invite.csv-import.advanced.validation.inbox-not-exist',
              {
                inboxName: nonExistentInboxes[0],
              },
            );
            hasInvalidRow = true;
            break;
          default:
            reason = this.intl.t(
              'settings.teammates.invite.csv-import.advanced.validation.multiple-inboxes-not-exist',
              {
                inboxNames: nonExistentInboxes.join(', '),
              },
            );
            hasInvalidRow = true;
        }
      }

      let rowWithEmojis = row
        .split(',')
        .map((inbox) => {
          let inboxName = inbox.trim().toLowerCase();
          let emoji = inboxEmojiMap.get(inboxName);
          return emoji ? `${emoji} ${inboxName}` : inboxName;
        })
        .join(', ');

      return { value: rowWithEmojis, reason };
    });
    return { header: this.normaliseHeader(headerName), hasInvalidRow, rows: validatedRows };
  }

  parseCopilotUsage(copilotUsageRows: string[]): ValidatedColumn {
    let hasInvalidRow = false;
    let validValues = ['off', 'included', 'unlimited'];
    let validatedRows: ValidatedRow[] = copilotUsageRows.map((row) => {
      let reason = undefined;
      if (!validValues.includes(row.toLowerCase().trim())) {
        reason = this.intl.t(
          'settings.teammates.invite.csv-import.advanced.validation.invalid-copilot-usage',
        );
        hasInvalidRow = true;
      }
      return { value: row, reason };
    });
    return { hasInvalidRow, header: this.normaliseHeader('copilot_usage'), rows: validatedRows };
  }

  parseAssignmentLimit(assignmentRows: string[], headerName: string): ValidatedColumn {
    let hasInvalidRow = false;
    let isTicketLimit = headerName === 'ticket_assignment_limit';
    let limitType = isTicketLimit ? 'ticket-assignment-limit' : 'conversation-assignment-limit';
    let validatedRows: ValidatedRow[] = assignmentRows.map((row) => {
      let reason = undefined;
      let numberRow = +row;
      if (row.trim() === '') {
        reason = this.intl.t(
          `settings.teammates.invite.csv-import.advanced.validation.${limitType}-empty`,
        );
      } else if (isNaN(numberRow)) {
        reason = this.intl.t(
          `settings.teammates.invite.csv-import.advanced.validation.${limitType}-invalid`,
        );
      } else if (numberRow < 0 || numberRow % 1 !== 0) {
        reason = this.intl.t(
          `settings.teammates.invite.csv-import.advanced.validation.${limitType}-invalid`,
        );
      } else if (numberRow > 1000000) {
        reason = this.intl.t(
          `settings.teammates.invite.csv-import.advanced.validation.${limitType}-max`,
        );
      }
      if (reason) {
        hasInvalidRow = true;
      }
      return { value: row, reason };
    });
    return { hasInvalidRow, header: this.normaliseHeader(headerName), rows: validatedRows };
  }

  genericRow(inboxRows: string[], headerName: string): ValidatedColumn {
    return {
      header: this.normaliseHeader(headerName),
      hasInvalidRow: false,
      rows: inboxRows.map((row) => {
        return { value: row, reason: undefined };
      }),
    };
  }

  normaliseHeader(header: string): string {
    return header.replaceAll('_', ' ');
  }

  @action
  async onConfirm() {
    // Create FormData to send the CSV file
    let formData = new FormData();
    formData.append('csv_file', this.model.file);
    formData.append('app_id', this.appService.app.id);

    try {
      // Make API call to create_from_csv endpoint
      await ajax({
        type: 'POST',
        url: '/ember/invite_admin_groups/create_from_csv',
        data: formData,
        contentType: false,
        processData: false,
      });

      this.router.transitionTo('apps.app.settings.workspace.teammates', {
        queryParams: { tab: 'invited' },
      });
    } catch (error) {
      console.error(error);
      let errorMessage = this.intl.t(
        'settings.teammates.invite.csv-import.advanced.notification.import-error',
      );

      // Check if the error response contains specific error messages
      if (error?.jqXHR?.responseJSON?.errors?.length > 0) {
        errorMessage = error.jqXHR.responseJSON.errors.join('\n');
      }

      this.notificationsService.notifyError(errorMessage);
    }
  }
}
